"use client"

import Image from "next/image"
import { useState } from "react"

import * as PopoverPrimitive from "@radix-ui/react-popover"
import { ChevronDown, SearchIcon } from "lucide-react"

import { Checkbox } from "@/components/ui/checkbox"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput
} from "@/components/ui/input-group"
import type { CasinoProviderDTO } from "@/domain/casino/list-lobby-facets"
import { cn } from "@/lib/utils"

import { lobbyTexts } from "../localization/texts"
import { FilterTriggerLabel } from "./FilterTriggerLabel"

export type ProviderFilterProps = {
  providers: CasinoProviderDTO[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ProviderFilter({
  providers,
  open,
  onOpenChange
}: ProviderFilterProps) {
  const [selected, setSelected] = useState<string[]>([])
  const [query, setQuery] = useState("")

  const normalizedQuery = query.trim().toLowerCase()
  const visibleProviders = providers.filter((p) => {
    if (!normalizedQuery) return true
    return p.name.toLowerCase().includes(normalizedQuery)
  })

  const triggerValue =
    selected.length === 0
      ? lobbyTexts.filters.providerAll
      : selected
          .map(
            (code) =>
              providers.find((p) => p.productCode === code)?.name ?? code
          )
          .join(", ")

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setQuery("")
        }
        onOpenChange(next)
      }}
    >
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          className="flex h-11 w-full min-w-0 items-center justify-between overflow-hidden rounded-[10px] border border-black-600 bg-black-800 px-3 text-sm font-medium text-white focus:border-black-600 focus:outline-hidden focus:ring-0 data-[state=open]:border-black-600 data-[state=open]:bg-black-600"
        >
          <FilterTriggerLabel
            prefix={lobbyTexts.filters.providers}
            value={triggerValue}
          />
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className="z-50 w-[calc(100vw-20px)] rounded-[10px] border border-black-600 bg-black-800 p-2 text-white shadow-md outline-hidden md:w-[calc(var(--radix-popover-trigger-width)+1.5rem)]"
        >
          <div className="flex flex-col gap-2">
            <InputGroup>
              <InputGroupAddon align="inline-start" className="pl-3 text-white">
                <SearchIcon
                  className="size-6 shrink-0 opacity-50"
                  aria-hidden
                />
              </InputGroupAddon>
              <InputGroupInput
                className="pl-2"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value)
                }}
                placeholder={lobbyTexts.filters.providerSearch}
                aria-label={lobbyTexts.filters.providerSearch}
              />
            </InputGroup>

            <div className="scrollbar-brand flex max-h-[11.75rem] flex-col gap-0.5 overflow-y-auto pr-3">
              {visibleProviders.length === 0 ? (
                <p className="px-2 py-3 text-center text-sm text-white/50">
                  {lobbyTexts.filters.providerEmpty}
                </p>
              ) : (
                visibleProviders.map((provider) => {
                  const isChecked = selected.includes(provider.productCode)

                  return (
                    <label
                      key={provider.productCode}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-[10px] px-2 py-2 text-sm",
                        isChecked ? "bg-black-600" : "hover:bg-black-800"
                      )}
                    >
                      <Checkbox
                        variant="brand"
                        size="sm"
                        className="data-[state=unchecked]:border-white/10 data-[state=unchecked]:bg-black-600"
                        checked={isChecked}
                        onCheckedChange={() => {
                          setSelected((current) => {
                            if (current.includes(provider.productCode)) {
                              return current.filter(
                                (c) => c !== provider.productCode
                              )
                            }
                            return [...current, provider.productCode]
                          })
                        }}
                      />
                      {provider.logoUrl ? (
                        <Image
                          src={provider.logoUrl}
                          alt=""
                          width={24}
                          height={13}
                          unoptimized
                          className="h-[13px] w-[24px] shrink-0 object-contain"
                          aria-hidden
                        />
                      ) : (
                        <span className="size-6 shrink-0" aria-hidden />
                      )}
                      <span
                        className={cn(
                          "min-w-0 flex-1 truncate",
                          isChecked ? "text-white" : "text-white/50"
                        )}
                      >
                        {provider.name}
                      </span>
                      <span className="shrink-0 rounded-full bg-purple-500 px-1.5 py-0.5 text-xs font-semibold text-white">
                        {provider.gameCount}
                      </span>
                    </label>
                  )
                })
              )}
            </div>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
